import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { showError, showSuccess } from '../utils/alerts';
import '../styles/pages/ControlClientesPage.css';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const ESTADO_LABEL = { pendiente: 'Pendiente', completada: 'Completada', omitida: 'Omitida' };
const ESTADO_ICONO = { pendiente: '○', completada: '✓', omitida: '–' };

const RESPONSABLE_LABEL = { renata: 'Renata', cliente: 'Cliente', humano: 'Humano' };
const RESPONSABLE_DESC = {
  renata: 'Operación automática',
  cliente: 'Entrega información',
  humano: 'Revisión del equipo'
};

const semaforo = (empresa) => {
  if (Number(empresa.total_tareas) === 0) return 'gris';
  if (Number(empresa.dias_plazo_vencidos) > 0) return 'rojo';
  if (Number(empresa.completadas) === Number(empresa.total_tareas)) return 'verde';
  return 'amarillo';
};

const ControlClientesPage = () => {
  const { token } = useAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [automatizacionActiva, setAutomatizacionActiva] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [detalleTareas, setDetalleTareas] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const anios = Array.from({ length: 3 }, (_, i) => hoy.getFullYear() - 1 + i);

  const fetchResumen = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/api/control-clientes/resumen`, {
        ...authHeaders,
        params: { anio, mes }
      });
      setEmpresas(data.empresas || []);
    } catch (error) {
      console.error('Error al obtener resumen de control clientes:', error);
      showError('Error al cargar el control de clientes');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anio, mes, token]);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/control-clientes/config`, authHeaders);
      setAutomatizacionActiva(data.automatizacion_activa);
    } catch (error) {
      console.error('Error al obtener configuración de control clientes:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchDetalle = useCallback(async (idCompany) => {
    try {
      setLoadingDetalle(true);
      const { data } = await axios.get(`${API_BASE_URL}/api/control-clientes/companies/${idCompany}/ciclo`, {
        ...authHeaders,
        params: { anio, mes }
      });
      setDetalleTareas(data.tareas || []);
    } catch (error) {
      console.error('Error al obtener el detalle del ciclo:', error);
      showError('Error al cargar el detalle del ciclo');
    } finally {
      setLoadingDetalle(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anio, mes, token]);

  useEffect(() => { fetchResumen(); }, [fetchResumen]);
  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => {
    if (selectedCompanyId) fetchDetalle(selectedCompanyId);
  }, [selectedCompanyId, fetchDetalle]);

  const handleSelectCompany = (idCompany) => {
    setSelectedCompanyId(prev => (prev === idCompany ? null : idCompany));
  };

  const handleToggleAutomatizacion = async () => {
    const activar = !automatizacionActiva;
    const confirmacion = await Swal.fire({
      icon: 'warning',
      title: activar ? '¿Activar automatización?' : '¿Desactivar automatización?',
      text: activar
        ? 'Se reanudarán los correos automáticos de solicitud y recordatorios para todas tus empresas.'
        : 'Dejarás de recibir/enviar correos automáticos de solicitud y recordatorios para todas tus empresas. El seguimiento manual del panel seguirá disponible.',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar'
    });
    if (!confirmacion.isConfirmed) return;

    try {
      await axios.put(
        `${API_BASE_URL}/api/control-clientes/config`,
        { automatizacion_activa: activar },
        authHeaders
      );
      setAutomatizacionActiva(activar);
      showSuccess(activar ? 'Automatización activada' : 'Automatización desactivada');
    } catch (error) {
      console.error('Error al actualizar configuración:', error);
      showError('No se pudo actualizar la configuración');
    }
  };

  const handleMarcarTarea = async (tarea, nuevoEstado) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/api/control-clientes/companies/${selectedCompanyId}/tareas/${tarea.id_tarea}`,
        { estado: nuevoEstado, anio, mes },
        authHeaders
      );
      await fetchDetalle(selectedCompanyId);
      await fetchResumen();
    } catch (error) {
      console.error('Error al marcar tarea:', error);
      showError(error.response?.data?.message || 'No se pudo actualizar la tarea');
    }
  };

  return (
    <div className="control-clientes-page">
      <div className="cc-header">
        <h1>Control Clientes</h1>
        <p className="cc-subtitle">
          Avance del ciclo mensual de recolección de información, Previred e IVA para todas tus empresas.
        </p>
      </div>

      <div className="cc-toolbar">
        <div className="cc-periodo-selector">
          <label>Mes:</label>
          <select value={mes} onChange={(e) => setMes(parseInt(e.target.value))}>
            {MESES.map((nombre, i) => <option key={i} value={i + 1}>{nombre}</option>)}
          </select>
          <label>Año:</label>
          <select value={anio} onChange={(e) => setAnio(parseInt(e.target.value))}>
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div className="cc-toggle">
          <span>Automatización de recordatorios:</span>
          <button
            className={`cc-toggle-btn ${automatizacionActiva ? 'activa' : 'inactiva'}`}
            onClick={handleToggleAutomatizacion}
          >
            {automatizacionActiva ? 'Activada' : 'Desactivada'}
          </button>
        </div>
      </div>

      {loading ? (
        <p>Cargando...</p>
      ) : empresas.length === 0 ? (
        <p>No tienes empresas registradas todavía.</p>
      ) : (
        <div className="cc-empresas-grid">
          {empresas.map(empresa => (
            <div
              key={empresa.id_company}
              className={`cc-empresa-card cc-${semaforo(empresa)} ${selectedCompanyId === empresa.id_company ? 'seleccionada' : ''}`}
              onClick={() => handleSelectCompany(empresa.id_company)}
            >
              <div className="cc-empresa-nombre">{empresa.company_name}</div>
              <div className="cc-empresa-progreso">
                {empresa.completadas} / {empresa.total_tareas || 12} tareas completadas
              </div>
              {Number(empresa.dias_plazo_vencidos) > 0 && (
                <div className="cc-empresa-alerta">Tiene tareas atrasadas</div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedCompanyId && (
        <div className="cc-detalle">
          <h2>Checklist del ciclo — {MESES[mes - 1]} {anio}</h2>

          <div className="cc-leyenda">
            {Object.keys(RESPONSABLE_LABEL).map(codigo => (
              <div key={codigo} className="cc-leyenda-item">
                <span className={`cc-leyenda-dot cc-leyenda-dot--${codigo}`} />
                <strong>{RESPONSABLE_LABEL[codigo]}</strong>
                <span className="cc-leyenda-desc">{RESPONSABLE_DESC[codigo]}</span>
              </div>
            ))}
          </div>

          {loadingDetalle ? (
            <p>Cargando checklist...</p>
          ) : (
            <ul className="cc-checklist">
              {detalleTareas.map(tarea => (
                <li
                  key={tarea.id_tarea}
                  className={`cc-tarea cc-tarea-${tarea.estado || 'pendiente'} cc-tarea--${tarea.responsable}`}
                >
                  <div className="cc-tarea-info">
                    <span className="cc-tarea-orden" title={ESTADO_LABEL[tarea.estado] || 'Pendiente'}>
                      {ESTADO_ICONO[tarea.estado] || ESTADO_ICONO.pendiente}
                    </span>
                    <div>
                      <div className="cc-tarea-nombre">{tarea.nombre}</div>
                      <div className="cc-tarea-meta">
                        Plazo: día {tarea.dia_plazo}
                        {tarea.tipo_ejecucion === 'notificacion' && ' · 15:00 hrs'}
                        {tarea.tipo_ejecucion !== 'notificacion' && tarea.hora_plazo && ` · hasta las ${tarea.hora_plazo.slice(0, 5)} hrs`}
                        {' '}· {ESTADO_LABEL[tarea.estado] || 'Pendiente'}
                        {tarea.origen === 'automatica' && ' (detectado automáticamente)'}
                      </div>
                    </div>
                  </div>
                  {tarea.tipo_ejecucion === 'manual' && (
                    <div className="cc-tarea-acciones">
                      {tarea.estado !== 'completada' && (
                        <button onClick={() => handleMarcarTarea(tarea, 'completada')}>Marcar completada</button>
                      )}
                      {tarea.estado && tarea.estado !== 'pendiente' && (
                        <button onClick={() => handleMarcarTarea(tarea, 'pendiente')}>Revertir a pendiente</button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ControlClientesPage;
